/**
 * CallCard.tsx — the pinned call card (GUI v2 feature 1).
 *
 * Always at the top, never scrolls away. Ghost (amber/dashed) = detected but
 * unconfirmed; solid = confirmed. Every field is one tap to correct (req 1.5):
 * names get an inline edit, vehicles a fast year/make/model/body picker, reason
 * chips a confirm/remove choice (req A.8). Color semantics per §10.1.
 */
import { useState } from "react";
import type { BodyStyle, CallCardState, ReasonKey, ServiceInterest, VehicleState } from "@/types";
import { CLASS_LABEL } from "@/live/content/pricing";

export const REASON_LABELS: Record<ReasonKey, string> = {
  heat: "Heat",
  glare: "Glare",
  privacy: "Privacy",
  uv_protection: "UV / skin",
  kids: "Kids",
  appearance: "Looks",
  interior_protection: "Interior",
  new_vehicle: "New vehicle",
  had_tint_before: "Had tint",
  comparing_quotes: "Comparing",
  paint_protection: "Paint",
  resale_value: "Resale",
  easier_maintenance: "Easy clean",
  fuel_costs: "Fuel",
};

const SERVICE_LABELS: Record<ServiceInterest, string> = {
  auto_tint: "Tint",
  ppf: "PPF",
  ceramic_coating: "Coating",
  bundle: "Bundle",
  unsure: "Unsure",
};

const BODY_STYLES: BodyStyle[] = [
  "sedan", "coupe", "hatchback", "convertible", "wagon", "truck", "suv", "crossover", "van", "minivan",
];

export interface CallCardActions {
  setName(field: "firstName" | "lastName", value: string): void;
  setVehicle(index: number, patch: { year?: number | null; make?: string | null; model?: string | null; bodyStyle?: BodyStyle | null }): void;
  setServiceInterest(value: ServiceInterest): void;
  setActiveVehicle(index: number): void;
  reason(key: ReasonKey, state: "confirmed" | "removed"): void;
}

function ghost(confirmed: boolean): string {
  return confirmed ? "text-foreground" : "text-amber-700 dark:text-amber-400 italic";
}

export function CallCard({ card, actions, compact }: { card: CallCardState; actions: CallCardActions; compact?: boolean }) {
  const [editingName, setEditingName] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(false);
  const vehicle = card.vehicles[card.activeVehicle] ?? card.vehicles[0];
  const fullName = [card.firstName.value, card.lastName.value].filter(Boolean).join(" ");
  const nameConfirmed = card.firstName.confirmed || card.firstName.source === "manual";

  return (
    <section aria-label="Call card" className="rounded-lg border bg-card p-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {/* Customer name — inline edit on tap */}
          {editingName ? (
            <NameEditor
              first={card.firstName.value ?? ""}
              last={card.lastName.value ?? ""}
              onSave={(f, l) => {
                actions.setName("firstName", f);
                actions.setName("lastName", l);
                setEditingName(false);
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setEditingName(true)}
              className="block max-w-full truncate text-left text-lg font-semibold leading-tight"
              title="Tap to correct the name"
            >
              <span className={ghost(nameConfirmed)}>{fullName || "Customer name…"}</span>
              {fullName && !nameConfirmed && <span className="ml-1 text-amber-500">?</span>}
            </button>
          )}
          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
            {card.phone.value && <span>{card.phone.value}</span>}
            <ServicePicker value={card.serviceInterest.value} confirmed={card.serviceInterest.confirmed} onPick={actions.setServiceInterest} />
          </div>
        </div>
        {/* Vehicle chips (req 1.8) */}
        <div className="flex shrink-0 flex-col items-end gap-1">
          {card.vehicles.map((v, i) => (
            <VehicleChip
              key={i}
              vehicle={v}
              active={i === card.activeVehicle}
              onTap={() => {
                if (i === card.activeVehicle) setEditingVehicle(true);
                else actions.setActiveVehicle(i);
              }}
            />
          ))}
        </div>
      </div>

      {editingVehicle && vehicle && (
        <VehicleEditor
          vehicle={vehicle}
          onSave={(patch) => {
            actions.setVehicle(card.activeVehicle, patch);
            setEditingVehicle(false);
          }}
          onCancel={() => setEditingVehicle(false)}
        />
      )}

      {/* Reason chips (req 1.7 / A.8) */}
      {!compact && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {card.reasons.map((r) => (
            <ReasonChipView key={r.key} reason={r} onAction={actions.reason} />
          ))}
          {card.reasons.length === 0 && (
            <span className="text-xs text-muted-foreground">Reasons appear here as the customer talks…</span>
          )}
        </div>
      )}
    </section>
  );
}

function NameEditor({ first, last, onSave }: { first: string; last: string; onSave: (f: string, l: string) => void }) {
  const [f, setF] = useState(first);
  const [l, setL] = useState(last);
  return (
    <form
      className="flex items-center gap-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(f.trim(), l.trim());
      }}
    >
      <input autoFocus value={f} onChange={(e) => setF(e.target.value)} placeholder="First" className="w-24 rounded-md border bg-background px-2 py-1 text-sm" />
      <input value={l} onChange={(e) => setL(e.target.value)} placeholder="Last" className="w-24 rounded-md border bg-background px-2 py-1 text-sm" />
      <button type="submit" className="rounded-md bg-primary px-2.5 py-1 text-sm font-medium text-primary-foreground">✓</button>
    </form>
  );
}

function VehicleChip({ vehicle, active, onTap }: { vehicle: VehicleState; active: boolean; onTap: () => void }) {
  const label =
    [vehicle.year.value, vehicle.make.value, vehicle.model.value].filter((v) => v !== null).join(" ") || "Add vehicle";
  const confirmed = vehicle.model.confirmed || vehicle.model.source === "manual";
  return (
    <button
      type="button"
      onClick={onTap}
      className={[
        "max-w-[220px] truncate rounded-full border px-2.5 py-1 text-sm font-medium",
        active ? "border-primary/50 bg-accent" : "opacity-70",
        confirmed ? "" : "border-dashed border-amber-400 text-amber-700 dark:text-amber-400",
      ].join(" ")}
      title="Tap to correct the vehicle"
    >
      {label}
      {vehicle.pricingClass && (
        <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">{CLASS_LABEL[vehicle.pricingClass]}{vehicle.classAssumed ? "?" : ""}</span>
      )}
      {vehicle.ambiguous && <span className="ml-1 text-amber-500">⚠</span>}
    </button>
  );
}

function VehicleEditor({
  vehicle,
  onSave,
  onCancel,
}: {
  vehicle: VehicleState;
  onSave: (patch: { year?: number | null; make?: string | null; model?: string | null; bodyStyle?: BodyStyle | null }) => void;
  onCancel: () => void;
}) {
  const [year, setYear] = useState(vehicle.year.value?.toString() ?? "");
  const [make, setMake] = useState(vehicle.make.value ?? "");
  const [model, setModel] = useState(vehicle.model.value ?? "");
  const [body, setBody] = useState<BodyStyle | "">(vehicle.bodyStyle.value ?? "");
  return (
    <form
      className="mt-2 flex flex-wrap items-center gap-1.5 rounded-md border bg-muted/40 p-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          year: year ? Number(year) : null,
          make: make.trim() || null,
          model: model.trim() || null,
          bodyStyle: body || null,
        });
      }}
    >
      <input value={year} onChange={(e) => setYear(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="Year" inputMode="numeric" className="w-16 rounded-md border bg-background px-2 py-1.5 text-sm" />
      <input value={make} onChange={(e) => setMake(e.target.value)} placeholder="Make" className="w-24 rounded-md border bg-background px-2 py-1.5 text-sm" />
      <input value={model} onChange={(e) => setModel(e.target.value)} placeholder="Model" className="w-28 rounded-md border bg-background px-2 py-1.5 text-sm" />
      <select value={body} onChange={(e) => setBody(e.target.value as BodyStyle | "")} className="rounded-md border bg-background px-2 py-1.5 text-sm">
        <option value="">Body…</option>
        {BODY_STYLES.map((b) => (
          <option key={b} value={b}>{b}</option>
        ))}
      </select>
      <button type="submit" className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground">Save</button>
      <button type="button" onClick={onCancel} className="rounded-md border px-3 py-1.5 text-sm">Cancel</button>
    </form>
  );
}

function ServicePicker({ value, confirmed, onPick }: { value: ServiceInterest | null; confirmed: boolean; onPick: (v: ServiceInterest) => void }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className={`rounded-full border px-2 py-0.5 text-xs font-medium ${value ? ghost(confirmed) : "text-muted-foreground"}`}>
        {value ? SERVICE_LABELS[value] : "Service?"}
      </button>
    );
  }
  return (
    <span className="flex flex-wrap gap-1">
      {(Object.keys(SERVICE_LABELS) as ServiceInterest[]).map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => {
            onPick(s);
            setOpen(false);
          }}
          className="rounded-full border px-2 py-0.5 text-xs font-medium hover:bg-accent"
        >
          {SERVICE_LABELS[s]}
        </button>
      ))}
    </span>
  );
}

function ReasonChipView({ reason, onAction }: { reason: { key: ReasonKey; confirmed: boolean }; onAction: (key: ReasonKey, state: "confirmed" | "removed") => void }) {
  const [asking, setAsking] = useState(false);
  if (asking && !reason.confirmed) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-xs dark:bg-amber-950/30">
        {REASON_LABELS[reason.key]}
        <button type="button" className="rounded-full bg-emerald-500 px-1.5 font-semibold text-white" onClick={() => { onAction(reason.key, "confirmed"); setAsking(false); }}>✓</button>
        <button type="button" className="rounded-full bg-muted px-1.5 font-semibold" onClick={() => { onAction(reason.key, "removed"); setAsking(false); }}>✕</button>
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => (reason.confirmed ? undefined : setAsking(true))}
      className={[
        "rounded-full px-2.5 py-1 text-xs font-medium",
        reason.confirmed
          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
          : "border border-dashed border-amber-400 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300",
      ].join(" ")}
    >
      {REASON_LABELS[reason.key]}
      {!reason.confirmed && "?"}
    </button>
  );
}
